import { useCallback, useEffect, useState } from 'react';
import PageContainer from '../../../components/Redesing/page-container';
import { Paper, Stack } from '@mui/material';
import ResumeDashboard from './resume';
import CardLoader from '../../../components/Redesing/card-loader';
import { IHardware } from '../../../ts/interface/hardware.interface';
import DashboardMap from '../dashboardMap';
import Controls from '../assets/controls';
import {
  IChartData,
  IDashboardGraphConfig,
} from '../../../ts/interface/dashboardGraph.interface';
import PageLoader from '../../../components/Redesing/page-loader';
import YearGraphs from '../assets/yearGraphs';
import { useOrgGraphStatsRelay } from '../../../hooks/dashaboard/query/V2/useOrgGraphStatsRelay';

import { formatDateOffsetISO } from '../../../utils';
import MonthGraphs from '../assets/month-graphs';
import useStation from '../../../hooks/stations/query/useStation';
import { LocationStatus } from '../../../ts/enums';
import { IStation } from '../../../ts/interface/station.interface';
import useCustomGraphStats from './useGraph';
import { daysInMonth } from '../../../util/utilsR';
import useAllHardware from '../../../hooks/hardware/query/organization/useAllHardware';
import { LOST, MISSING, RETIRED } from '../../../constants';
import useOrganization from '../../../hooks/use-organization';

const timeZones = require('../../../../data/timezones.json');

function OrganizationDashboard() {
  const organizationInfo = useOrganization();

  const [currentYear, setCurrentYear] = useState<number>(0);
  const [timePeriod, setTimePeriod] = useState<string>('month');
  const [currentMonth, setCurrentMonth] = useState<number>(0);
  const [graphStatsConfig, setGraphStatsConfig] = useState<IDashboardGraphConfig>({
    type: 'month',
  });
  const [pathLoaded, setPathLoaded] = useState<boolean>(false);
  const [allHardwareLoaded, setAllHardwareLoaded] = useState<IHardware[]>([]);

  const getOptionDateConfig = ({
    type,
    customMonth,
    customYear,
  }: {
    type: string;
    customMonth?: number;
    customYear?: number;
  }) => {
    const timezone = 'America/New_York';
    const date = new Date();
    const year = customYear ?? date.getFullYear();
    let month = customMonth ?? date.getMonth();
    let start;
    let end;
    const systemTimezone = timeZones.find((zone: { utc: string[] }) =>
      zone.utc.find((name: string) => name === timezone)
    );
    const offset = systemTimezone?.text.slice(4, 10);
    const monthSelected = month + 1;
    const monthFormat = monthSelected < 10 ? `0${monthSelected}` : monthSelected;
    if (type === 'month') {
      month += 1;
      start = formatDateOffsetISO(year, monthFormat, '01', '00:00:00', offset);
      end = formatDateOffsetISO(
        year,
        monthFormat,
        daysInMonth(month, year, false),
        '23:59:00',
        offset
      );
    } else {
      start = formatDateOffsetISO(year, '01', '01', '00:00:00', offset);
      end = formatDateOffsetISO(year, '12', '31', '23:59:00', offset);
    }
    return {
      end,
      start,
      type,
      organization_id: organizationInfo?.id,
    };
  };

  const dateConfig = getOptionDateConfig(graphStatsConfig);
  const {
    data: graphStats,
    stats: graphStatsCounters,
    isLoading: isLoadingGraphStats,
  } = useOrgGraphStatsRelay({
    organizationId: organizationInfo?.id || '',
    startDate: dateConfig.start,
    endDate: dateConfig.end,
    granularity: dateConfig.type,
    enabled: !!organizationInfo?.id,
  });

  const {
    yearArray,
    rentalData,
    rentalAvg,
    userAvg,
    userData,
    userTotal,
    durationAvg,
    durationData,
    systemTimezoneLabel,
  } = useCustomGraphStats({
    isLoadingGraphStats,
    graphStats,
    timePeriod,
    currentMonth,
    currentYear,
    organizationId: organizationInfo.id,
  });

  const { data: stat, isLoading: isLoadingStations } = useStation(
    {
      enabled: true,
    },
    {
      status: LocationStatus.all,
      organization_id: organizationInfo.id,
    }
  );

  const stations = stat?.data || ([] as IStation[]);

  const { data: defaultHardwareData, refetch: refetchHardware } = useAllHardware(
    {
      enabled: !pathLoaded,
    },
    {
      organization_id: organizationInfo.id,
      paginationResultHandler: (res: { data: IHardware[]; hasMore: boolean }) => {
        setPathLoaded(false);
        const { data, hasMore } = res;
        setAllHardwareLoaded((prevData) => [...prevData, ...data]);
        setPathLoaded(hasMore);
      },
    }
  );

  useEffect(() => {
    if (defaultHardwareData) {
      setAllHardwareLoaded((prevData) => [
        ...prevData,
        ...(defaultHardwareData?.data || []),
      ]);
    }
  }, [defaultHardwareData, pathLoaded]);

  useEffect(() => {
    refetchHardware().then(() => setPathLoaded(true));
  }, [organizationInfo.id, refetchHardware]);

  const updatePeriod = (
    keyOld: string,
    valueOld: string,
    updateStates: boolean,
    obj: any
  ) => {
    const { key, currentYear, currentMonth, timePeriod } = obj;
    let year;
    if (key === 'currentYear') {
      setCurrentYear(currentYear);
      year = yearArray[currentYear];
    } else {
      year = yearArray[currentYear];
    }
    if (updateStates) {
      setTimePeriod(timePeriod);
      setCurrentMonth(currentMonth);
    }

    setGraphStatsConfig({
      type: timePeriod,
      customMonth: currentMonth,
      customYear: year,
    });
  };

  const isLoaded = useCallback(() => {
    return isLoadingStations || !pathLoaded;
  }, [pathLoaded, isLoadingStations]);

  const filteredHardware = allHardwareLoaded.filter((unit: IHardware | any) => {
    const isRetired = unit.state === RETIRED;
    const hasValidLocation = unit.last_lat && unit.last_lng && unit.state !== RETIRED;
    const isMissingOrLostWithoutLocation =
      (unit.state === MISSING || unit.state === LOST) && unit.current_station === null;
    return hasValidLocation && !isRetired && !isMissingOrLostWithoutLocation;
  }) as unknown as IHardware[];

  return (
    <PageContainer spaceName="Dashboard">
      <Stack direction={'column'} spacing={2}>
        <Stack>
          <ResumeDashboard
            organizationId={organizationInfo.id || ''}
            stats={graphStatsCounters}
            isLoading={isLoadingGraphStats}
          />
        </Stack>

        <Stack>
          {isLoaded() ? (
            <CardLoader useCard />
          ) : (
            <DashboardMap
              isLoading={isLoaded()}
              stations={stations as unknown as IStation[]}
              units={filteredHardware as IHardware[]}
              hardware_type={'bike'}
              systemAccess={organizationInfo.access || 0}
            />
          )}
        </Stack>

        <Stack>
          <Paper sx={{ borderRadius: '20px' }}>
            <Stack pl={2} pt={2}>
              <Controls
                timePeriod={timePeriod}
                yearArray={yearArray}
                currentMonth={currentMonth}
                //@ts-ignore
                updateTimePeriod={updatePeriod}
              />
            </Stack>

            {isLoadingGraphStats ? (
              <PageLoader />
            ) : (
              <Stack>
                <Stack>
                  {timePeriod === 'year' && graphStats && (
                    <YearGraphs
                      rentalTotal={graphStats?.durations?.total_rentals}
                      rentalAvg={rentalAvg}
                      rentalData={rentalData as IChartData}
                      userTotal={userTotal}
                      userAvg={userAvg}
                      userData={userData as IChartData}
                      durationTotal={graphStats?.durations?.total_duration || 0}
                      durationAvg={durationAvg}
                      durationData={durationData as IChartData}
                      systemTimezoneLabel={systemTimezoneLabel}
                    />
                  )}
                  {timePeriod === 'month' && graphStats && (
                    <MonthGraphs
                      rentalTotal={graphStats?.durations?.total_rentals}
                      rentalAvg={rentalAvg}
                      rentalData={rentalData as IChartData}
                      userTotal={userTotal}
                      userAvg={userAvg}
                      userData={userData as IChartData}
                      durationTotal={graphStats?.durations?.total_duration || 0}
                      durationAvg={durationAvg}
                      durationData={durationData as IChartData}
                      systemTimezoneLabel={systemTimezoneLabel}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </Paper>
        </Stack>
      </Stack>
    </PageContainer>
  );
}

export default OrganizationDashboard;
